import { motion, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

import {
  FaHandshake,
  FaUserTie,
  FaUsers,
  FaChalkboardTeacher,
  FaGraduationCap,
  FaProjectDiagram,
} from "react-icons/fa";

/* =========================================================
   VERIFIED / APPROVED BUSINESS METRICS

   Keep these numbers only if IA4Solutions can substantiate
   them for public use.
========================================================= */

const presenceItems = [
  {
    id: 1,
    icon: FaHandshake,
    value: 50,
    label: "Industry Collaborations",
    description: "Industry connections",
  },
  {
    id: 2,
    icon: FaUserTie,
    value: 1000,
    label: "Students Placed",
    description: "Career outcomes",
  },
  {
    id: 3,
    icon: FaChalkboardTeacher,
    value: 20,
    label: "Expert Mentors",
    description: "Training expertise",
  },
  {
    id: 4,
    icon: FaUsers,
    value: 5000,
    label: "Students Trained",
    description: "Learning community",
  },
  {
    id: 5,
    icon: FaGraduationCap,
    value: 50,
    label: "Training Programs",
    description: "Career-focused programs",
  },
  {
    id: 6,
    icon: FaProjectDiagram,
    value: 1000,
    label: "Projects Completed",
    description: "Practical experience",
  },
];

/* =========================================================
   COUNT UP
========================================================= */

const useCountUp = (target, isInView, duration = 1400) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView || target === null || target === undefined) {
      return;
    }

    let frame;
    let startTime = null;

    const animate = (timestamp) => {
      if (startTime === null) {
        startTime = timestamp;
      }

      const progress = Math.min(
        (timestamp - startTime) / duration,
        1
      );

      const eased =
        1 - Math.pow(1 - progress, 3);

      setCount(Math.floor(target * eased));

      if (progress < 1) {
        frame = requestAnimationFrame(animate);
      } else {
        setCount(target);
      }
    };

    frame = requestAnimationFrame(animate);

    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [isInView, target, duration]);

  return count;
};

/* =========================================================
   METRIC ITEM
========================================================= */

const ImpactMetric = ({
  item,
  index,
  shouldReduceMotion,
}) => {
  const ref = useRef(null);

  const isInView = useInView(ref, {
    once: true,
    margin: "0px 0px -50px 0px",
  });

  const count = useCountUp(
    item.value,
    isInView
  );

  const Icon = item.icon;

  return (
    <motion.article
      ref={ref}
      initial={
        shouldReduceMotion
          ? { opacity: 1 }
          : { opacity: 0, y: 12 }
      }
      whileInView={
        shouldReduceMotion
          ? undefined
          : { opacity: 1, y: 0 }
      }
      viewport={{ once: true }}
      transition={{
        duration: 0.45,
        delay: shouldReduceMotion
          ? 0
          : index * 0.05,
        ease: "easeOut",
      }}
      className="
        group
        relative
        min-w-0
        px-4
        py-5
        transition-colors
        duration-300
        sm:px-5
        sm:py-6
        lg:px-6
        lg:py-7
      "
    >
      {/* Top accent */}
      <span
        aria-hidden="true"
        className="
          absolute
          left-4
          top-0
          h-[2px]
          w-6
          rounded-full
          bg-gradient-to-r
          from-blue-500
          to-cyan-400
          opacity-50
          transition-all
          duration-300
          group-hover:w-10
          group-hover:opacity-100
          sm:left-5
          lg:left-6
        "
      />

      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className="
            mt-0.5
            flex
            h-9
            w-9
            shrink-0
            items-center
            justify-center
            rounded-lg
            border
            border-blue-100
            bg-blue-50
            text-blue-600
            transition-all
            duration-300
            group-hover:border-cyan-200
            group-hover:bg-cyan-50
            group-hover:text-cyan-600
            dark:border-blue-900/40
            dark:bg-blue-950/50
            dark:text-blue-400
            dark:group-hover:border-cyan-900/50
            dark:group-hover:bg-cyan-950/40
            dark:group-hover:text-cyan-400
          "
        >
          <Icon
            aria-hidden="true"
            className="text-sm"
          />
        </div>

        <div className="min-w-0">
          {/* Number */}
          <p
            className="
              text-[clamp(1.65rem,3vw,2.1rem)]
              font-extrabold
              leading-none
              tracking-[-0.035em]
              text-gray-950
              dark:text-white
            "
            aria-label={`${item.value.toLocaleString()} plus ${item.label}`}
          >
            {count.toLocaleString()}
            <span
              className="
                ml-0.5
                text-blue-600
                dark:text-blue-400
              "
            >
              +
            </span>
          </p>

          {/* Metric label */}
          <h3
            className="
              mt-2
              text-xs
              font-bold
              leading-snug
              text-gray-800
              dark:text-gray-100
              sm:text-sm
            "
          >
            {item.label}
          </h3>

          {/* Supporting text */}
          <p
            className="
              mt-1
              text-[10px]
              leading-4
              text-gray-500
              dark:text-gray-400
              sm:text-[11px]
            "
          >
            {item.description}
          </p>
        </div>
      </div>
    </motion.article>
  );
};

/* =========================================================
   MAIN SECTION
========================================================= */

const PresenceSection = () => {
  const shouldReduceMotion = useReducedMotion();

  return (
    <section
      aria-labelledby="impact-heading"
      className="
        relative
        overflow-hidden
        bg-white
        px-5
        py-8
        text-gray-900
        sm:px-6
        sm:py-10
        md:px-10
        md:py-12
        lg:px-16
        lg:py-14
        dark:bg-gray-950
        dark:text-white
      "
    >
      {/* =====================================================
          SUBTLE AMBIENT BACKGROUND
      ===================================================== */}

      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
      >
        {/* Blue light */}
        <div
          className="
            absolute
            -left-28
            top-10
            h-60
            w-60
            rounded-full
            bg-blue-500/[0.035]
            blur-3xl
            dark:bg-blue-500/[0.04]
          "
        />

        {/* Cyan light */}
        <div
          className="
            absolute
            -right-24
            bottom-0
            h-64
            w-64
            rounded-full
            bg-cyan-500/[0.03]
            blur-3xl
            dark:bg-cyan-500/[0.035]
          "
        />

        {/* Technical grid */}
        <div
          className="
            absolute
            inset-0
            opacity-[0.015]
            dark:opacity-[0.02]
            [background-image:linear-gradient(to_right,#64748b_1px,transparent_1px),linear-gradient(to_bottom,#64748b_1px,transparent_1px)]
            [background-size:48px_48px]
          "
        />
      </div>

      {/* =====================================================
          MAIN PANEL
      ===================================================== */}

      <div
        className="
          relative
          mx-auto
          max-w-6xl
          overflow-hidden
          rounded-2xl
          border
          border-gray-200
          bg-slate-50/80
          shadow-[0_12px_40px_-22px_rgba(15,23,42,0.18)]
          dark:border-gray-800
          dark:bg-gray-900/80
          dark:shadow-[0_18px_50px_-28px_rgba(0,0,0,0.5)]
        "
      >
        {/* Top accent */}
        <div
          aria-hidden="true"
          className="
            absolute
            left-0
            right-0
            top-0
            h-[2px]
            bg-gradient-to-r
            from-blue-500/0
            via-blue-500/50
            to-cyan-400/0
          "
        />

        {/* ===================================================
            HEADER
        =================================================== */}

        <motion.header
          initial={
            shouldReduceMotion
              ? { opacity: 1 }
              : { opacity: 0, y: 12 }
          }
          whileInView={
            shouldReduceMotion
              ? undefined
              : { opacity: 1, y: 0 }
          }
          viewport={{
            once: true,
            margin: "0px 0px -50px 0px",
          }}
          transition={{
            duration: 0.45,
            ease: "easeOut",
          }}
          className="
            px-5
            pb-5
            pt-6
            text-center
            sm:px-7
            sm:pb-6
            sm:pt-7
            md:px-8
            md:pt-8
          "
        >
          {/* Section label */}

          <div className="flex items-center justify-center gap-2.5">
            <span
              aria-hidden="true"
              className="
                h-1.5
                w-1.5
                rounded-full
                bg-blue-500
              "
            />

            <span
              className="
                text-[10px]
                font-bold
                uppercase
                tracking-[0.2em]
                text-blue-600
                dark:text-blue-400
                sm:text-xs
              "
            >
              Our Impact
            </span>
          </div>

          {/* Heading */}

          <h2
            id="impact-heading"
            className="
              mt-2.5
              text-2xl
              font-extrabold
              leading-tight
              tracking-[-0.025em]
              text-blue-900
              dark:text-white
              sm:text-3xl
              md:text-4xl
            "
          >
            Results That Matter
          </h2>

          {/* Description */}

          <p
            className="
              mx-auto
              mt-2.5
              max-w-xl
              text-sm
              leading-6
              text-gray-600
              dark:text-gray-300
              sm:text-[15px]
            "
          >
            Our growing community reflects our focus on practical
            learning, industry connections, mentorship, and career
            development.
          </p>
        </motion.header>

        {/* ===================================================
            METRICS
        =================================================== */}

        <div
          className="
            border-t
            border-gray-200
            dark:border-gray-800
          "
        >
          <div
            className="
              grid
              grid-cols-2
              divide-x
              divide-y
              divide-gray-200
              md:grid-cols-3
              md:divide-y-0
              dark:divide-gray-800
            "
          >
            {presenceItems.map((item, index) => (
              <ImpactMetric
                key={item.id}
                item={item}
                index={index}
                shouldReduceMotion={shouldReduceMotion}
              />
            ))}
          </div>
        </div>

        {/* ===================================================
            BOTTOM LINE
        =================================================== */}

        <div
          className="
            flex
            items-center
            justify-center
            gap-2
            border-t
            border-gray-200
            px-5
            py-3
            dark:border-gray-800
          "
        >
          <span
            aria-hidden="true"
            className="
              h-1.5
              w-1.5
              rounded-full
              bg-emerald-500
            "
          />

          <p
            className="
              text-[9px]
              font-semibold
              uppercase
              tracking-[0.12em]
              text-gray-400
              dark:text-gray-500
              sm:text-[10px]
            "
          >
            Learning · Projects · Careers
          </p>
        </div>
      </div>
    </section>
  );
};

export default PresenceSection;